import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Star } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

const EMPTY_FILTERED = "No hay resultados que coincidan con el filtro aplicado.";
const EMPTY_ALL = "No hay postulaciones disponibles.";

const cellClass = "flex min-w-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-border bg-background p-3 text-center text-sm";
const titleCellClass =
  "sticky left-0 z-10 flex min-w-[250px] flex-col justify-center gap-2 rounded-lg border-r-2 border-border bg-muted p-3 text-left text-sm font-semibold text-foreground";

const PostulacionesDesktop = ({
  sortedPostulaciones,
  tenderInfo,
  columnas,
  downloadFile,
  confirmSelectWinner,
  preguntas,
  filterConfig,
  setFilterConfig,
  sortConfig,
  handleSort,
  openFilterModal,
  postulaciones,
}) => {
  const navigate = useNavigate();

  const hayPostulacionesFiltradas = postulaciones?.postulaciones?.length > 0;

  // Renderiza las celdas de una fila, o el estado vacío correspondiente.
  const renderCells = (renderCell) =>
    sortedPostulaciones.length > 0 ? (
      sortedPostulaciones.map((postulacion, index) => (
        <div key={index} className={cellClass}>
          {renderCell(postulacion, index)}
        </div>
      ))
    ) : (
      <div className="col-span-full rounded-lg border border-dashed border-border bg-muted/40 p-5 text-center text-sm italic text-muted-foreground">
        {hayPostulacionesFiltradas ? EMPTY_FILTERED : EMPTY_ALL}
      </div>
    );

  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-2.5 pt-5"
        style={{ gridTemplateColumns: `250px repeat(${columnas}, minmax(200px, 1fr))` }}
      >
        {/* Fila Acciones */}
        <div className="contents">
          <div className={titleCellClass}>Acciones</div>
          {renderCells((postulacion) => (
            <>
              <Button
                size="sm"
                onClick={() =>
                  navigate(`/tender/${postulacion.id_tender}/application/${postulacion.id_application}`)
                }
              >
                Ver Postulación Completa
              </Button>

              {tenderInfo?.tender?.ganador === postulacion.id_application ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin-contrato/${postulacion.id_application}`)}
                >
                  <Star className="h-3.5 w-3.5" /> Ganador
                </Button>
              ) : (
                confirmSelectWinner &&
                tenderInfo?.tender?.estado !== "licitacion-ganada" && (
                  <Button size="sm" variant="outline" onClick={() => confirmSelectWinner(postulacion)}>
                    Escoger como ganador
                  </Button>
                )
              )}
            </>
          ))}
        </div>

        {/* Fila de Postulantes */}
        <div className="contents">
          <div className={titleCellClass}>Postulantes</div>
          {renderCells((postulacion) => (
            <>
              <p>
                <strong className="text-foreground">Postulación ID:</strong> {postulacion.id_application}
              </p>
              {postulacion.company_postulante && (
                <p>
                  <Link
                    to={`/company-profile/${postulacion.company_postulante.id_company}/${encodeURIComponent(
                      postulacion.company_postulante.nombre_empresa
                    )}`}
                    className="font-semibold text-primary hover:underline"
                  >
                    {postulacion.company_postulante.nombre_empresa.length > 20
                      ? `${postulacion.company_postulante.nombre_empresa.slice(0, 20)}...`
                      : postulacion.company_postulante.nombre_empresa}
                  </Link>
                </p>
              )}
              <p>
                <Link
                  to={`/profile/${postulacion.user_postulante.id_user}/${encodeURIComponent(
                    `${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`
                  )}`}
                  className="font-semibold text-primary hover:underline"
                >
                  {`${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`.length > 15
                    ? `${`${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`.slice(0, 15)}...`
                    : `${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`}
                </Link>
              </p>
              <p>
                <strong className="text-foreground">Cel:</strong>{" "}
                {`+${postulacion.user_postulante.codigo_pais} ${postulacion.user_postulante.celular}`.length > 15
                  ? `${`+${postulacion.user_postulante.codigo_pais} ${postulacion.user_postulante.celular}`.slice(0, 15)}...`
                  : `+${postulacion.user_postulante.codigo_pais} ${postulacion.user_postulante.celular}`}
              </p>
              <p>
                {postulacion.user_postulante.correo.length > 20
                  ? `${postulacion.user_postulante.correo.slice(0, 20)}...`
                  : postulacion.user_postulante.correo}
              </p>
            </>
          ))}
        </div>

        {/* Propuesta de los postulantes */}
        <div className="contents">
          <div className={titleCellClass}>Propuesta de los postulantes</div>
          {renderCells((postulacion) => (
            <p>
              {postulacion.detalles_oferta.length > 100
                ? `${postulacion.detalles_oferta.slice(0, 100)}...`
                : postulacion.detalles_oferta}
            </p>
          ))}
        </div>

        {/* Documentos Adjuntos */}
        <div className="contents">
          <div className={titleCellClass}>Documentos adjuntos</div>
          {renderCells((postulacion) =>
            postulacion.archivos_adjuntos && postulacion.archivos_adjuntos.length > 0 ? (
              <ul className="flex flex-wrap justify-center gap-2">
                {postulacion.archivos_adjuntos.map((archivo, archivoIndex) => (
                  <li key={archivoIndex}>
                    <button
                      onClick={() => downloadFile(archivo.id_file)}
                      title={archivo.file_name}
                      className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-primary hover:underline"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {archivo.file_name.length > 20 ? `${archivo.file_name.slice(0, 20)}...` : archivo.file_name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <span>—</span>
            )
          )}
        </div>

        {/* Filas de preguntas dinámicas */}
        {preguntas.map(({ question, type }, index) => (
          <div key={index} className="contents">
            <div className={titleCellClass}>
              <span>
                {index + 1}. {question} ({type})
              </span>

              {type === "Respuesta de Sí o No" && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={filterConfig[question] ? "default" : "outline"}
                    onClick={() =>
                      setFilterConfig((prev) => {
                        const currentState = prev[question] || null;
                        const nextState =
                          currentState === null ? "Sí" : currentState === "Sí" ? "No" : null;

                        const updatedConfig = { ...prev };
                        if (nextState) {
                          updatedConfig[question] = nextState;
                        } else {
                          delete updatedConfig[question];
                        }
                        return updatedConfig;
                      })
                    }
                  >
                    {(() => {
                      const label = { null: "Filtrar", Sí: "Solo Sí", No: "Solo No" };
                      return label[filterConfig[question] || null];
                    })()}
                  </Button>
                  <Button
                    size="sm"
                    variant={sortConfig.find((config) => config.question === question) ? "default" : "outline"}
                    onClick={() => handleSort(question, type)}
                  >
                    {(() => {
                      const config = sortConfig.find((config) => config.question === question);
                      if (!config) return "Ordenar";
                      return config.order === "asc" ? "No primero" : "Sí primero";
                    })()}
                  </Button>
                </div>
              )}

              {type === "Solo permite números" && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={filterConfig[question] ? "default" : "outline"}
                    onClick={() => openFilterModal(question, type)}
                  >
                    {filterConfig[question] ? "Filtrado" : "Filtrar"}
                  </Button>
                  <Button
                    size="sm"
                    variant={sortConfig.find((config) => config.question === question) ? "default" : "outline"}
                    onClick={() => handleSort(question, type)}
                  >
                    {(() => {
                      const config = sortConfig.find((config) => config.question === question);
                      if (!config) return "Ordenar";
                      return config.order === "asc" ? "Menor a Mayor" : "Mayor a Menor";
                    })()}
                  </Button>
                </div>
              )}

              {type === "Solo archivos adjuntos" && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={filterConfig[question] ? "default" : "outline"}
                    onClick={() =>
                      setFilterConfig((prev) => {
                        const currentState = prev[question] || null;
                        const nextState =
                          currentState === null
                            ? "conArchivo"
                            : currentState === "conArchivo"
                            ? "sinArchivo"
                            : null;

                        const updatedConfig = { ...prev };
                        if (nextState) {
                          updatedConfig[question] = nextState;
                        } else {
                          delete updatedConfig[question];
                        }
                        return updatedConfig;
                      })
                    }
                  >
                    {(() => {
                      const label = { null: "Filtrar", conArchivo: "Con archivos", sinArchivo: "Sin archivos" };
                      return label[filterConfig[question] || null];
                    })()}
                  </Button>
                  <Button
                    size="sm"
                    variant={sortConfig.find((config) => config.question === question) ? "default" : "outline"}
                    onClick={() => handleSort(question, type)}
                  >
                    {(() => {
                      const config = sortConfig.find((config) => config.question === question);
                      if (!config) return "Ordenar";
                      switch (config.order) {
                        case "asc":
                          return "Adjuntos primero";
                        case "desc":
                          return "Adjuntos al final";
                        default:
                          return "Ordenar";
                      }
                    })()}
                  </Button>
                </div>
              )}
            </div>

            {renderCells((postulacion) => {
              const respuesta = postulacion.respuestas_preguntas_licitador[question];
              return type === "Solo archivos adjuntos" ? (
                respuesta && respuesta.file_path ? (
                  <button
                    onClick={() => downloadFile(respuesta.id_file)}
                    title={respuesta.file_name}
                    className={cn("flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-primary hover:underline")}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {respuesta.file_name.length > 20 ? `${respuesta.file_name.slice(0, 17)}...` : respuesta.file_name}
                  </button>
                ) : (
                  "Sin documento adjunto"
                )
              ) : (
                respuesta || "No respondida"
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PostulacionesDesktop;
